import classNames from 'classnames';
import { noop } from 'lodash';
import * as React from 'react';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import Dropdown from 'components/Toolbar/Shared/Dropdown';
import { ContentTypes, Layer } from 'const';
import { Priority, useClickOutsideWithToggle } from 'hooks/useClickOutside';
import { intlGet } from 'utils/intlGet';
import { TranslationControlProps } from './models';
import styles from './styles.module.scss';

const Translation: React.FunctionComponent<TranslationControlProps> = ({
  activeLayer,
  disabled,
  originalLang,
  language,
  toggleLayer,
  onImport,
  onExport,
  onAITranslate,
}) => {
  const { container, on, toggle } = useClickOutsideWithToggle<HTMLDivElement>(Priority.HEADER, { useToolbar: false });
  const isOriginalActive = activeLayer === Layer.ORIGINAL;
  const importDisabled = !onImport;
  const exportDisabled = !onExport;
  const aiTranslationDisabled = !onAITranslate;

  React.useEffect(() => {
    if (disabled && on) {
      toggle();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, toggle]);

  const handleImport: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (importDisabled) {
      return;
    }

    const packageFile = event.target.files?.item(0);

    if (packageFile) {
      onImport(packageFile);
    }
    toggle();
  };

  const handleExport: React.MouseEventHandler<HTMLDivElement> = () => {
    if (exportDisabled) {
      return;
    }

    onExport();
    toggle();
  };

  const handleAITranslation: React.MouseEventHandler<HTMLDivElement> = () => {
    if (aiTranslationDisabled) {
      return;
    }

    onAITranslate();
    toggle();
  };

  const toggleOriginalLayer = (): void => {
    toggleLayer(Layer.ORIGINAL);
    toggle();
  };

  const toggleTranslatedLayer = (): void => {
    toggleLayer(Layer.TRANSLATED);
    toggle();
  };

  const renderTargetLabel = (): JSX.Element => <span className={styles.targetLabel}>{intlGet('Translation', 'Target')}</span>;
  const renderSourceLabel = (): JSX.Element => <span className={styles.sourceLabel}>{intlGet('Translation', 'Source')}</span>;
  const renderMarker = (layer: Layer): JSX.Element => (
    <span className={styles.marker}>
      {
        layer === activeLayer
          ? <Icon color="primary" size="badge" type={IconType.TICK} />
          : null
      }
    </span>
  );

  return (
    <div
      className={classNames(styles.TranslationControl, {
        [styles.disabled]: disabled,
      })}
      ref={container}
    >
      <AccessibleDiv className={styles.header} onClick={!disabled ? toggle : noop}>
        <Button
          icon={IconType.TRANSLATION}
          size="sm"
          title={isOriginalActive ? originalLang : language}
          disabled={disabled}
          highlightOnHover={true}
        />
        {isOriginalActive ? renderSourceLabel() : renderTargetLabel()}
      </AccessibleDiv>
      {
        on &&
        <Dropdown className={styles.dropdown}>
          <AccessibleDiv className={styles.language} onClick={toggleTranslatedLayer}>
            {renderMarker(Layer.TRANSLATED)}
            <span className={styles.value}>{language}</span>
            {renderTargetLabel()}
          </AccessibleDiv>
          <AccessibleDiv className={styles.language} onClick={toggleOriginalLayer}>
            {renderMarker(Layer.ORIGINAL)}
            <span className={styles.value}>{originalLang}</span>
            {renderSourceLabel()}
          </AccessibleDiv>
          <div className={styles.separator}/>
          <AccessibleDiv
            className={classNames(styles.option, {
              [styles.disabled]: aiTranslationDisabled,
            })}
            onClick={handleAITranslation}
          >
            {intlGet('Translation', 'AITranslation')}
          </AccessibleDiv>
          <div className={styles.separator}/>
          <label className={styles.label}>
            <div
              className={classNames(styles.option, {
                [styles.disabled]: importDisabled,
              })}
            >
              {intlGet('Translation', 'ImportPackage')}
            </div>
            <input
              className={styles.fileInput}
              disabled={importDisabled}
              onChange={handleImport}
              accept={ContentTypes.ZIP}
              type="file"
            />
          </label>
          <AccessibleDiv
            className={classNames(styles.option, {
              [styles.disabled]: exportDisabled,
            })}
            onClick={handleExport}
          >
            {intlGet('Translation', 'ExportPackage')}
          </AccessibleDiv>
        </Dropdown>
      }
    </div>
  );
};

export default Translation;
